'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, Rocket, Planet, Globe } from '@phosphor-icons/react';
import { Project } from '@/types/project';

interface NavigationProps {
  projects: Project[];
  onProjectSelect: (project: Project) => void;
  selectedProject: Project | null;
}

export default function Navigation({ projects, onProjectSelect, selectedProject }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Logo e Menu Toggle */}
      <div className="absolute top-4 sm:top-6 lg:top-8 left-4 sm:left-6 lg:left-8 z-40">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center space-x-3 sm:space-x-4"
        >
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
              <img
                src="/logo.svg"
                alt="Nexuniverse Logo"
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="hidden sm:block">
              <h1 className="text-white font-bold text-lg sm:text-xl tracking-wider">NEXUniverse</h1>
              <p className="text-gray-400 text-xs tracking-widest">Desenvolvimento Digital</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Menu Toggle */}
      <motion.button
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={() => setIsOpen(!isOpen)}
        className="absolute top-4 sm:top-6 lg:top-8 right-4 sm:right-6 lg:right-8 z-50 p-3 sm:p-4 bg-black/20 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/40 hover:border-white/20 transition-all duration-300 min-h-[48px] min-w-[48px] touch-manipulation"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? (
          <X size={20} weight="light" className="text-white sm:w-6 sm:h-6" />
        ) : (
          <List size={20} weight="light" className="text-white sm:w-6 sm:h-6" />
        )}
      </motion.button>

      {/* Menu Lateral */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-full sm:w-96 lg:w-[28rem] bg-black/90 backdrop-blur-md border-l border-white/10 z-40 overflow-y-auto"
            >
              <div className="p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <div className="mb-8 sm:mb-10 lg:mb-12 pt-16 sm:pt-20">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-wider">Universos</h2>
                  <p className="text-gray-400 text-sm sm:text-base font-light leading-relaxed">
                    Explore nossos projetos digitais e descubra as tecnologias que moldam o futuro.
                  </p>
                </div>

                {/* Lista de Projetos */}
                <div className="space-y-4 sm:space-y-6">
                  {projects.map((project, index) => (
                    <motion.button
                      key={project.id}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => {
                        onProjectSelect(project);
                        setIsOpen(false);
                      }}
                      className={`w-full p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border transition-all text-left group relative overflow-hidden min-h-[80px] touch-manipulation ${selectedProject?.id === project.id
                        ? 'bg-white/5 border-white/20'
                        : 'bg-white/[0.02] border-white/5 hover:bg-white/5 hover:border-white/15'
                        }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Background gradient */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      <div className="relative flex items-start space-x-3 sm:space-x-4">
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border"
                            style={{
                              backgroundColor: project.color,
                              borderColor: project.color,
                              opacity: 0.8
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium mb-1 sm:mb-2 group-hover:text-gray-200 transition-colors text-base sm:text-lg">
                            {project.name}
                          </h3>
                          <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 leading-relaxed font-light">
                            {project.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <span className="text-xs px-2 sm:px-3 py-1 rounded-full border border-white/10 text-gray-300 font-medium">
                              {project.mainTech}
                            </span>
                            <span className="text-gray-500 text-xs font-light">
                              +{project.technologies.length - 1} techs
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-8 sm:mt-10 lg:mt-12 pt-6 sm:pt-8 border-t border-white/10">
                  <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-400">
                    <div className="flex items-center space-x-2">
                      <Planet size={14} weight="light" className="sm:w-4 sm:h-4" />
                      <span className="text-sm font-medium">{projects.length} Universos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Globe size={14} weight="light" className="sm:w-4 sm:h-4" />
                      <span className="text-sm font-medium">Portfolio</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Indicador de Projeto Selecionado */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 transform -translate-x-1/2 z-30 px-4"
          >
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-4 sm:px-6 py-2 sm:py-3 flex items-center space-x-2 sm:space-x-3 max-w-xs sm:max-w-none">
              <div
                className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: selectedProject.color }}
              />
              <span className="text-white font-medium tracking-wide text-sm sm:text-base truncate">{selectedProject.name}</span>
              <Globe size={14} weight="light" className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}